import { TableRow, TableCell } from "@/components/ui/table";
import { Cell, Row } from "@tanstack/react-table";
import { memo, useMemo } from "react";
import { ProjectSchedulesTableCell } from "@/modules/projects/components/schedules/ProjectSchedulesTableCell";
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { ScheduleWithVisitMap } from "@/modules/projects/components/schedules/types";
import { ProjectSchedulesTableSelectRowCell } from "./ProjectSchedulesTableSelectRowCell";

interface SchedulesTableRowProps {
  row: Row<ScheduleWithVisitMap>;
  onRowMouseEnterInternal: (id: string) => () => void;
  onRowMouseLeaveInternal: (id: string) => () => void;
  cells: Cell<ScheduleWithVisitMap, unknown>[];
  virtualRow: VirtualItem;
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>;
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  fixedColumnCount: number;
}

function SchedulesTableRow({
  row,
  onRowMouseEnterInternal,
  onRowMouseLeaveInternal,
  virtualRow,
  cells,
  columnVirtualizer,
  virtualPaddingLeft,
  virtualPaddingRight,
  fixedColumnCount,
}: SchedulesTableRowProps) {
  const style = useMemo(
    () => ({
      transform: `var(--row-transform-${virtualRow.index})`,
      backgroundColor: `var(--row-bg-${row.id})`,
    }),
    [virtualRow.index, row.id],
  );

  const virtualColumns = columnVirtualizer.getVirtualItems();

  const virtualLeftStyle = useMemo(
    () => ({
      display: "flex",
      width: virtualPaddingLeft,
    }),
    [virtualPaddingLeft],
  );

  const virtualStyleRight = useMemo(
    () => ({
      display: "flex",
      width: virtualPaddingRight,
    }),
    [virtualPaddingRight],
  );

  return (
    <TableRow
      className="absolute p-0 flex w-full"
      style={style}
      onMouseEnter={onRowMouseEnterInternal(row.id)}
      onMouseLeave={onRowMouseLeaveInternal(row.id)}
      data-index={virtualRow.index}
    >
      <ProjectSchedulesTableSelectRowCell
        cell={cells[0]}
        id={row.original.id}
      />
      {cells.slice(1, fixedColumnCount).map((cell) => {
        return <ProjectSchedulesTableCell key={cell.id} cell={cell} />;
      })}
      {/* Virtual padding left */}
      {virtualPaddingLeft ? <TableCell style={virtualLeftStyle} /> : null}
      {/* Render virtualized day columns */}
      {virtualColumns.map((virtualColumn) => {
        const cell = cells[fixedColumnCount + virtualColumn.index];
        return <ProjectSchedulesTableCell key={cell.id} cell={cell} />;
      })}
      {/* Virtual padding right */}
      {virtualPaddingRight ? <TableCell style={virtualStyleRight} /> : null}
    </TableRow>
  );
}

export default memo(SchedulesTableRow);
